import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import AssetSaleService from '../../services/assetSale.service';
import { getError, showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button } from '../../components/EntityList';

function formatDate(date) {
	return date ? new Date(date).toLocaleDateString('es-UY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AssetSales({ assetId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const [deletingAssetSale, setDeletingAssetSale] = useState(null);

	const handleAddAssetSale = () => {
		navigate(`/AdminScreen/assets/asset/${assetId}/assetSale`);
	};

	const handleEditAssetSale = (assetSale) => {
		navigate(`/AdminScreen/assetSales/editAssetSale/${assetSale._id}`, {
			state: { returnTo: `/AdminScreen/assets/asset/${assetId}/sales` },
		});
	};

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((sale) => {
			const key = sale.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (sale.amount || 0));
		});
		return [
			{ label: t('entity.assetSale.plural'), value: rows.length },
			{
				label: t('field.amount'),
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows, t]);

	async function handleConfirmDelete() {
		try {
			await AssetSaleService.delete(deletingAssetSale._id);
			toast.success(t('msg.assetSaleDeleted'));
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
			toast.error(t('msg.assetSaleDeleteFailed'));
		} finally {
			setDeletingAssetSale(null);
		}
	}

	const columns = [
		{ key: 'id', header: t('field.id'), render: (sale) => <IdChip>{sale.customId}</IdChip> },
		{ key: 'date', header: t('field.date'), render: (sale) => formatDate(sale.date) },
		{ key: 'lot', header: t('field.lotUpper'), className: 'tw:font-mono', render: (sale) => sale.lot },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (sale) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(sale.currency, sale.amount)}</span>,
		},
		{ key: 'purchaser', header: t('entity.purchaser'), render: (sale) => sale.purchaser?.name },
		{
			key: 'sumDebts',
			header: t('field.totalDebts'),
			className: 'tw:text-right',
			render: (sale) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(sale.currency, sale.sumDebts)}</span>,
		},
		{
			key: 'initialPayment',
			header: t('field.initialPayment'),
			className: 'tw:text-right',
			render: (sale) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(sale.currency, sale.initialPayment)}</span>,
		},
	];

	const renderRowActions = (sale) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditAssetSale(sale) },
				{ label: t('action.delete'), onClick: () => setDeletingAssetSale(sale), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={handleAddAssetSale}>
					{t('action.add')} {t('entity.assetSale')}
				</Button>
			</div>
			<Section title={t('entity.assetSale.plural')}>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(sale) => navigate(`/AdminScreen/assetSales/assetSale/${sale._id}`)}
					rowActions={renderRowActions}
					emptyMessage={t('msg.noData')}
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingAssetSale)}
				handleClose={() => setDeletingAssetSale(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteAssetSale')}
				description={t('msg.confirmDeleteAssetSale')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AssetSales;
